import React from 'react';
import { ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import Text from './Text';

/**
 * The removable filter chip from the reference's marketplace toolbar
 * ("Living room ×", "Bedroom ×"), and the plain selectable chip used for
 * sports, courts and venues.
 *
 * Selected is UI green, never brand teal — picking a court is a selection, not
 * a call to action.
 */
export interface ChipProps {
  label: React.ReactNode;
  icon?: React.ReactNode;
  selected?: boolean;
  caret?: boolean;
  /** Renders an × that calls `onRemove` instead of selecting. */
  onRemove?: () => void;
  onClick?: () => void;
  size?: 'sm' | 'md';
  className?: string;
}

const Chip: React.FC<ChipProps> = ({
  label,
  icon,
  selected = false,
  caret = false,
  onRemove,
  onClick,
  size = 'md',
  className,
}) => {
  const compact = size === 'sm';

  const body = (
    <>
      {icon ? <span className="shrink-0 grid place-items-center">{icon}</span> : null}
      <Text variant={compact ? 'micro' : 'caption'} tone="inherit" as="span" className="truncate">
        {label}
      </Text>
      {caret ? <ChevronDown className="w-3.5 h-3.5 shrink-0 opacity-60" aria-hidden /> : null}
      {onRemove ? (
        <span
          role="button"
          tabIndex={0}
          aria-label="Remove"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onRemove();
            }
          }}
          className="grid place-items-center w-4 h-4 shrink-0 rounded-pill opacity-50 hover:opacity-100 transition-opacity"
        >
          <X className="w-3 h-3" aria-hidden />
        </span>
      ) : null}
    </>
  );

  const classes = cn(
    'inline-flex items-center gap-1.5 rounded-flup-sm border whitespace-nowrap transition-colors duration-150',
    compact ? 'h-7 px-2.5' : 'h-9 px-3',
    selected
      ? 'bg-flup-green-soft border-flup-green text-flup-green-ink'
      : 'bg-flup-surface border-flup-hairline text-flup-ink-2 hover:border-flup-hairline-2 hover:text-flup-ink',
    className,
  );

  if (!onClick) {
    return <span className={classes}>{body}</span>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        classes,
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flup-brand focus-visible:ring-offset-1',
      )}
    >
      {body}
    </button>
  );
};

export default React.memo(Chip);
